import { useState, useEffect, useCallback, useRef } from 'react';

interface MarketData {
  interestRates: {
    '30_year_fixed': number;
    '15_year_fixed': number;
    '5_1_arm': number;
    'va_30_year': number;
    'fha_30_year': number;
  };
  marketTrends: {
    homeValueIndex: number;
    changeMonth: number;
    changeYear: number;
    inventoryMonths: number;
    medianDaysOnMarket: number;
  };
  stockIndices: {
    nasdaq: { price: number; change: number; changePercent: number; };
    sp500: { price: number; change: number; changePercent: number; };
    dow: { price: number; change: number; changePercent: number; };
    russell2000: { price: number; change: number; changePercent: number; };
  };
  cryptoPrices: {
    btc: { price: number; change24h: number; changePercent24h: number; };
    eth: { price: number; change24h: number; changePercent24h: number; };
    usdt: { price: number; change24h: number; changePercent24h: number; };
    usdc: { price: number; change24h: number; changePercent24h: number; };
  };
  timestamp: string;
  lastUpdated?: string;
  source?: string;
  sources?: {
    rates: 'api_ninjas' | 'mock' | 'cache';
    stocks: 'fmp' | 'mock' | 'cache';
    crypto: 'coingecko' | 'mock' | 'cache';
    trends: 'redfin' | 'mock' | 'cache';
  };
  timestamps?: {
    rates?: string;
    stocks?: string;
    crypto?: string;
    trends?: string;
  };
}

interface PropertyAlert {
  alertType: string;
  property: {
    address: string;
    previousPrice: number;
    currentPrice: number;
    priceChange: number;
    percentChange: number;
  };
  timestamp: string;
}

export const useRealtimeMarketData = () => {
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [alerts, setAlerts] = useState<PropertyAlert[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();

  const connect = useCallback(() => {
    try {
      console.log('Connecting to real-time market data...');
      
      // Use the full WebSocket URL for the edge function
      wsRef.current = new WebSocket(
        'wss://njoxbqtqffsfhkwgbksu.functions.supabase.co/functions/v1/realtime-market-data'
      );

      wsRef.current.onopen = () => {
        console.log('Connected to real-time market data');
        setIsConnected(true);
        setError(null);
        
        // Clear any existing reconnect timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received market data:', data);
          
          switch (data.type) {
            case 'market_data':
              setMarketData({
                ...data.data,
                sources: {
                  rates: data.data.ratesSource || 'mock',
                  stocks: data.data.stocksSource || 'mock',
                  crypto: data.data.cryptoSource || 'mock',
                  trends: data.data.trendsSource || 'mock',
                },
                timestamps: {
                  rates: data.data.ratesLastUpdated,
                  stocks: data.data.stocksLastUpdated,
                  crypto: data.data.cryptoLastUpdated,
                  trends: data.data.trendsLastUpdated,
                }
              });
              break;
            case 'rate_update':
              setMarketData(prev => prev ? {
                ...prev,
                interestRates: data.data.interestRates,
                timestamp: data.data.timestamp,
                lastUpdated: data.data.lastUpdated,
                source: data.data.source
              } : null);
              break;
            case 'trend_update':
              setMarketData(prev => prev ? {
                ...prev,
                marketTrends: data.data.marketTrends,
                timestamp: data.data.timestamp
              } : null);
              break;
            case 'stock_update':
              setMarketData(prev => prev ? {
                ...prev,
                stockIndices: data.data.stockIndices,
                timestamp: data.data.timestamp
              } : null);
              break;
            case 'crypto_update':
              setMarketData(prev => prev ? {
                ...prev,
                cryptoPrices: data.data.cryptoPrices,
                timestamp: data.data.timestamp
              } : null);
              break;
            case 'property_alert':
              setAlerts(prev => [data.data, ...prev.slice(0, 9)]); // Keep last 10 alerts
              break;
            case 'subscription_confirmed':
              console.log('Subscription confirmed:', data.subscription);
              break;
            case 'error':
              console.error('WebSocket error:', data.message);
              setError(data.message);
              break;
            default:
              console.log('Unknown message type:', data.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          setError('Failed to parse market data');
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        console.error('WebSocket URL:', 'wss://njoxbqtqffsfhkwgbksu.functions.supabase.co/functions/v1/realtime-market-data');
        setError('Connection error - check console for details');
      };

      wsRef.current.onclose = (event) => {
        console.log('WebSocket connection closed:', event.code, event.reason);
        setIsConnected(false);
        
        // Attempt to reconnect after 5 seconds if not manually closed
        if (event.code !== 1000) {
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log('Attempting to reconnect...');
            connect();
          }, 5000);
        }
      };
    } catch (error) {
      console.error('Failed to establish WebSocket connection:', error);
      console.error('Connection details:', {
        url: 'wss://njoxbqtqffsfhkwgbksu.functions.supabase.co/functions/v1/realtime-market-data',
        error: error
      });
      setError('Failed to connect to market data - check console for details');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'Manual disconnect');
      wsRef.current = null;
    }
    
    setIsConnected(false);
  }, []);

  const subscribeToRates = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_rates',
        data: { notification: true }
      }));
    }
  }, []);

  const subscribeToTrends = useCallback((zipCode: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe_trends',
        data: { zipCode }
      }));
    }
  }, []);

  const requestPropertyAlerts = useCallback((criteria: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'get_property_alerts',
        data: criteria
      }));
    }
  }, []);

  // Auto-connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    marketData,
    alerts,
    isConnected,
    error,
    connect,
    disconnect,
    subscribeToRates,
    subscribeToTrends,
    requestPropertyAlerts
  };
};